import { trpcNoAuth } from '@prishapolicy/shared/trpc';
import {
  Button,
  ButtonPanel,
  Form,
  HeroBackground,
  LiveTextBox,
  Modal,
  TextInput,
  VersatileFilterAndSortRenderer,
  useFiltersAndSort,
  useFuzzySearch,
  productColorMapping,
  unslugify,
  useWindowWidth,
} from '@prishapolicy/shared/ui';
import { CallbackForm, SmallHorizontalCard, getFlattenedResourceData, resourceFilters } from '@prishapolicy/w3/ui';
import { NextSeo } from 'next-seo';
import { useState } from 'react';
import { object, string } from 'yup';

const resources = getFlattenedResourceData();

const RequestResource = () => {
  return (
    <div>
      <div className="bg-grey-100 grid">
        <div className="border-grey-200 border-b bg-white py-5 font-bold">
          <p>Can&apos;t find a resource?</p>
          <Button text="Request Resource" variant="secondary" className="mt-2 w-full" />
        </div>
      </div>
    </div>
  );
};

const ResourcesView = ({ query }: { query: string }) => {
  const { isMd } = useWindowWidth();
  const fAndS = useFiltersAndSort({
    data: resources,
    filters: resourceFilters,
  });
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const mutation = trpcNoAuth['lead-management']['lead']['create'].useMutation();

  const finalData = useFuzzySearch(fAndS.filteredData, query);
  return (
    <div className="container grid-cols-4 gap-x-12 py-16 lg:grid lg:px-10">
      <div>
        <div className="bg-grey-100 hidden gap-y-0.5 lg:grid">
          <div className="bg-white pb-10">
            <div className="flex justify-between">
              <div className="text-title-5 font-bold">Filter</div>
            </div>
            <VersatileFilterAndSortRenderer {...fAndS} />
          </div>
          <RequestResource />
          <CallbackForm />
        </div>
        <div className="grid grid-cols-12 lg:hidden">
          <div className="col-span-8">Showing {finalData.length} results</div>
          <div className="col-span-4 flex w-full justify-end">Filter</div>
        </div>
      </div>
      <Modal
        isOpen={activeIndex > -1}
        title={'Before you download!'}
        theme="primary"
        description="We need to know a little more about you. Our policy is not to sell or rent your information."
        closeModal={() => setActiveIndex(-1)}
        headerSide={isMd ? 'left' : 'top'}
      >
        <div className="container">
          <Form
            initialValues={{
              fullName: '',
              phone: '',
              email: '',
            }}
            validationSchema={object().shape({
              fullName: string().required('Full Name is required'),
              phone: string()
                .required('Phone number is required')
                .matches(/^\d{10}$/, 'Enter a valid phone number'),
              email: string()
                .required('Email is required')
                .matches(/\S+@\S+\.\S+/, 'Enter a valid Email address'),
            })}
            onSubmit={async (values) => {
              window.open(finalData[activeIndex].resourceLink);
              await mutation.mutateAsync({
                productType: undefined,
                metadata: {
                  personalDetails_fullName: values.fullName,
                  personalDetails_email: values.email,
                  personalDetails_phone: values.phone,
                },
                activityDate: undefined,
                leadActivityStatus: undefined,
                leadStatus: undefined,
                notes: undefined,
              });
              values.email = '';
              values.fullName = '';
              values.phone = '';
              setActiveIndex(-1);
            }}
          >
            <TextInput name="fullName" placeholder="Enter your full name" />
            <TextInput name="phone" placeholder="Enter your phone number" />
            <TextInput name="email" placeholder="Enter your email" />
            <br />
            <ButtonPanel
              primaryButtonInfo={{
                text: 'Download Now',
                type: 'submit',
              }}
              tertiaryButtonInfo={{
                text: 'Cancel',
                onClick: () => setActiveIndex(-1),
              }}
            />
          </Form>
        </div>
      </Modal>
      <div className="col-span-3 h-fit grid-cols-2 gap-2 lg:grid">
        {finalData.map((resource, idx) => {
          return (
            <SmallHorizontalCard
              key={idx}
              title={resource.name}
              description={resource.description}
              badges={[
                {
                  theme: productColorMapping[resource.insuranceCategory],
                  text: unslugify(resource.insuranceCategory.split('-')[0]),
                },
                {
                  theme: 'blue',
                  text: resource.resourceCategory,
                },
              ]}
              image={resource.image}
              idx={idx}
              setActiveIndex={setActiveIndex}
            />
          );
        })}
      </div>
      <div className="mt-15 flex flex-col lg:hidden">
        <RequestResource />
        <CallbackForm />
      </div>
    </div>
  );
};

const InsurerResources = () => {
  const [query, setQuery] = useState('');

  return (
    <>
      <NextSeo
        title="Insurer Resources | PrishaPolicy"
        description="One stop to get all the resources required to start with your insurance journey"
      />
      <HeroBackground backgroundColor="yellow-200">
        <div className="mt-16 mb-9 text-center lg:mx-auto lg:w-1/2">
          <h1 className="heading-1">Insurer Resources</h1>
          <h5 className="heading-5 mt-6 mb-9">
            One stop to get all the resources required to start with your insurance journey
          </h5>
          <div>
            <LiveTextBox className="bg-white" value={query} setValue={setQuery} />
          </div>
        </div>
      </HeroBackground>
      <ResourcesView query={query} />
    </>
  );
};

export default InsurerResources;
